import {
  formatDistanceToNow,
  format,
  formatRelative,
  isToday,
  isYesterday,
  isThisWeek,
  isThisYear,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
} from "date-fns";

// -----------------------------------------------------------
// NUMBER FORMATTING
// -----------------------------------------------------------

export function formatCount(n: number): string {
  if (n === 0) return "0";
  if (Math.abs(n) < 1000) return n.toString();
  if (Math.abs(n) < 1_000_000) {
    const k = n / 1000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  if (Math.abs(n) < 1_000_000_000) {
    const m = n / 1_000_000;
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  const b = n / 1_000_000_000;
  return `${b % 1 === 0 ? b.toFixed(0) : b.toFixed(1)}B`;
}

export function formatKarma(karma: number): string {
  if (karma < 0) return `${karma.toLocaleString()}`;
  return `+${karma.toLocaleString()}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

// -----------------------------------------------------------
// DATE / TIME FORMATTING
// -----------------------------------------------------------

export function formatTimeAgo(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const seconds = differenceInSeconds(now, d);

  if (seconds < 60) return "just now";

  const minutes = differenceInMinutes(now, d);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = differenceInHours(now, d);
  if (hours < 24) return `${hours}h ago`;

  if (isYesterday(d)) return "yesterday";
  if (isThisWeek(d)) return format(d, "EEEE");
  if (isThisYear(d)) return format(d, "MMM d");

  return format(d, "MMM d, yyyy");
}

export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return formatDistanceToNow(d, { addSuffix: true });
}

export function formatDateTime(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "MMM d, yyyy 'at' h:mm a");
}

export function formatDate(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "MMMM d, yyyy");
}

export function formatShortDate(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "MMM d, yyyy");
}

export function formatJoinDate(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "MMMM yyyy");
}

export function formatVideoDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// -----------------------------------------------------------
// STRING FORMATTING
// -----------------------------------------------------------

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function generateUniqueSlug(title: string, id: string): string {
  const base = slugify(title);
  const shortId = id.slice(0, 8);
  return `${base}-${shortId}`;
}

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) return `${count} ${singular}`;
  return `${count} ${plural || singular + "s"}`;
}

export function extractDomain(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace("www.", "");
  } catch {
    return url;
  }
}

export function sanitizeUsername(username: string): string {
  return username.toLowerCase().replace(/[^a-z0-9_.-]/g, "");
}

export function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  const masked = local.charAt(0) + "*".repeat(Math.max(local.length - 2, 1)) + local.charAt(local.length - 1);
  return `${masked}@${domain}`;
}

// -----------------------------------------------------------
// URL UTILITIES
// -----------------------------------------------------------

export function buildSearchUrl(query: string, params?: Record<string, string>): string {
  const searchParams = new URLSearchParams({ q: query, ...params });
  return `/search?${searchParams.toString()}`;
}

export function buildPostUrl(slug: string, id: string): string {
  return `/post/${id}/${slug}`;
}

export function buildProfileUrl(username: string): string {
  return `/profile/${username}`;
}

export function buildTopicUrl(slug: string): string {
  return `/topics/${slug}`;
}

export function getAvatarFallback(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// -----------------------------------------------------------
// VOTE SCORE FORMATTING
// -----------------------------------------------------------

export function formatVoteScore(score: number): string {
  if (score === 0) return "0";
  if (score > 0) return `+${formatCount(score)}`;
  return formatCount(score);
}

export function getVoteScoreColor(score: number): string {
  if (score > 100) return "text-brand";
  if (score > 0) return "text-brand/80";
  if (score < 0) return "text-destructive";
  return "text-text-muted";
}

// -----------------------------------------------------------
// CONTENT UTILITIES
// -----------------------------------------------------------

export function estimateReadTime(text: string): number {
  const wordsPerMinute = 200;
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
}

export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

export function highlightSearchTerms(text: string, query: string): string {
  if (!query.trim()) return text;
  const terms = query.trim().split(/\s+/);
  let highlighted = text;

  for (const term of terms) {
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    highlighted = highlighted.replace(regex, '<mark class="bg-brand/20 text-foreground rounded px-0.5">$1</mark>');
  }

  return highlighted;
}

// -----------------------------------------------------------
// VALIDATION HELPERS
// -----------------------------------------------------------

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function isValidUsername(username: string): boolean {
  return /^[a-zA-Z0-9_.-]{3,30}$/.test(username);
}

export function isStrongPassword(password: string): {
  isStrong: boolean;
  score: number;
  feedback: string[];
} {
  const feedback: string[] = [];
  let score = 0;

  if (password.length >= 8) score++;
  else feedback.push("Use at least 8 characters");

  if (password.length >= 12) score++;

  if (/[a-z]/.test(password)) score++;
  else feedback.push("Add lowercase letters");

  if (/[A-Z]/.test(password)) score++;
  else feedback.push("Add uppercase letters");

  if (/\d/.test(password)) score++;
  else feedback.push("Add numbers");

  if (/[^a-zA-Z0-9]/.test(password)) score++;
  else feedback.push("Add special characters");

  return {
    isStrong: score >= 4,
    score: Math.min(score, 5),
    feedback,
  };
}
